/* eslint-disable @typescript-eslint/no-explicit-any */

import { calculateDistance } from "./mapCalculations";

const ROUTE_SOURCE_ID = "navigation-route";
const ROUTE_LAYER_ID = "navigation-route-layer";
const CONNECTOR_SOURCE_ID = "navigation-connector";
const CONNECTOR_LAYER_ID = "navigation-connector-layer";

/** Minimum distance in meters to show connector dots */
const MIN_CONNECTOR_DISTANCE = 1;

export type AddRouteLayerParams = {
  /**
   * Mapbox map instance
   */
  map: any;
  /**
   * GeoJSON LineString feature representing the route
   */
  route: GeoJSON.Feature<GeoJSON.LineString>;
  /**
   * Origin coordinates [lat, lng] (main property marker)
   */
  origin: [number, number];
  /**
   * Destination coordinates [lat, lng] (amenity)
   */
  destination: [number, number];
  /**
   * Route line color (default: #3b82f6 - blue)
   */
  color?: string;
};

/**
 * Creates a GeoJSON LineString feature from two coordinates
 */
const createConnectorLine = (
  start: [number, number],
  end: [number, number],
): GeoJSON.Feature<GeoJSON.LineString> => ({
  type: "Feature",
  properties: {},
  geometry: {
    type: "LineString",
    coordinates: [start, end],
  },
});

/**
 * Adds a navigation route layer to the Mapbox map with optional connector dots
 * @param params - Configuration parameters for the route layer
 */
export const addRouteLayer = ({
  map,
  route,
  origin,
  destination,
  color = "#007BFF",
}: AddRouteLayerParams): void => {
  // Remove existing route if present
  removeRouteLayer(map);

  const coordinates = route.geometry.coordinates;
  const routeStart = coordinates[0] as [number, number]; // [lng, lat]
  const routeEnd = coordinates[coordinates.length - 1] as [number, number]; // [lng, lat]

  // Convert origin/destination from [lat, lng] to [lng, lat] for Mapbox
  const originLngLat: [number, number] = [origin[1], origin[0]];
  const destinationLngLat: [number, number] = [destination[1], destination[0]];

  // Calculate distances from route endpoints to actual coordinates
  const startDistance = calculateDistance(
    origin[0],
    origin[1],
    routeStart[1],
    routeStart[0],
  );
  const endDistance = calculateDistance(
    routeEnd[1],
    routeEnd[0],
    destination[0],
    destination[1],
  );

  // Build connector lines if needed
  const connectorCoordinates: [number, number][][] = [];

  if (startDistance > MIN_CONNECTOR_DISTANCE) {
    connectorCoordinates.push([originLngLat, routeStart]);
  }

  if (endDistance > MIN_CONNECTOR_DISTANCE) {
    connectorCoordinates.push([routeEnd, destinationLngLat]);
  }

  // Add main route layer
  map.addSource(ROUTE_SOURCE_ID, {
    type: "geojson",
    data: route,
  });

  map.addLayer({
    id: ROUTE_LAYER_ID,
    type: "line",
    source: ROUTE_SOURCE_ID,
    layout: {
      "line-join": "round",
      "line-cap": "round",
    },
    paint: {
      "line-color": color,
      "line-width": 4,
      "line-opacity": 0.8,
    },
  });

  // Add connector dots layer if needed
  if (connectorCoordinates.length > 0) {
    const connectorGeoJSON: GeoJSON.FeatureCollection = {
      type: "FeatureCollection",
      features: connectorCoordinates.map((coords) =>
        createConnectorLine(coords[0], coords[1]),
      ),
    };

    map.addSource(CONNECTOR_SOURCE_ID, {
      type: "geojson",
      data: connectorGeoJSON,
    });

    map.addLayer({
      id: CONNECTOR_LAYER_ID,
      type: "line",
      source: CONNECTOR_SOURCE_ID,
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
      paint: {
        "line-color": color,
        "line-width": 3,
        "line-opacity": 0.6,
        "line-dasharray": [2, 4],
      },
    });
  }
};

/**
 * Removes the navigation route layer from the Mapbox map
 * @param map - Mapbox map instance
 */
export const removeRouteLayer = (map: any): void => {
  // Remove connector layer
  if (map.getLayer(CONNECTOR_LAYER_ID)) {
    map.removeLayer(CONNECTOR_LAYER_ID);
  }
  if (map.getSource(CONNECTOR_SOURCE_ID)) {
    map.removeSource(CONNECTOR_SOURCE_ID);
  }

  // Remove main route layer
  if (map.getLayer(ROUTE_LAYER_ID)) {
    map.removeLayer(ROUTE_LAYER_ID);
  }
  if (map.getSource(ROUTE_SOURCE_ID)) {
    map.removeSource(ROUTE_SOURCE_ID);
  }
};
